import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Save, Upload, DollarSign, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPaymentSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    airtel_money_number: '',
    airtel_money_account_name: '',
    airtel_money_qr_url: '',
    upi_id: '',
    upi_qr_url: '',
    drc_plus_monthly: 10,
    drc_plus_annual: 100,
    drc_pro_monthly: 13,
    drc_pro_annual: 130,
    india_plus_monthly: 899,
    india_plus_annual: 8990,
    india_pro_monthly: 1299,
    india_pro_annual: 12990
  });

  useEffect(() => {
    checkAdminAccess();
    fetchSettings();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!response.data.is_admin) {
        // Silently redirect non-admin users
        navigate('/');
      }
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSettings(prev => ({
        ...prev,
        [field]: response.data.file_url
      }));

      toast.success('QR code uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload QR code');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/payment-settings`, settings, {
        withCredentials: true
      });
      toast.success('Payment settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="py-8 px-6 max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Settings</h1>
          <p className="text-gray-600">Configure payment methods and pricing</p>
        </div>

        <div className="space-y-6">
          {/* Airtel Money Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="text-3xl">🇨🇩</div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Airtel Money (DRC)</h2>
                <p className="text-sm text-gray-600">Payment details for DRC customers</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Airtel Money Number
                </label>
                <input
                  type="text"
                  value={settings.airtel_money_number || ''}
                  onChange={(e) => setSettings({...settings, airtel_money_number: e.target.value})}
                  placeholder="Enter Airtel Money number"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Account Name
                </label>
                <input
                  type="text"
                  value={settings.airtel_money_account_name || ''}
                  onChange={(e) => setSettings({...settings, airtel_money_account_name: e.target.value})}
                  placeholder="Enter account name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  QR Code
                </label>
                {settings.airtel_money_qr_url && (
                  <img 
                    src={settings.airtel_money_qr_url} 
                    alt="Airtel Money QR" 
                    className="w-48 h-48 border-2 border-gray-200 rounded-lg mb-3"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'airtel_money_qr_url')}
                  className="hidden"
                  id="airtel-qr-upload"
                />
                <label 
                  htmlFor="airtel-qr-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-all"
                >
                  <Upload className="w-4 h-4" />
                  Upload QR Code
                </label>
              </div>
            </div>
          </div>

          {/* UPI Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="text-3xl">🇮🇳</div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">UPI (India)</h2>
                <p className="text-sm text-gray-600">Payment details for Indian customers</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  UPI ID
                </label>
                <input
                  type="text"
                  value={settings.upi_id || ''}
                  onChange={(e) => setSettings({...settings, upi_id: e.target.value})}
                  placeholder="Enter UPI ID (e.g., yourname@paytm)"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  QR Code
                </label>
                {settings.upi_qr_url && (
                  <img 
                    src={settings.upi_qr_url} 
                    alt="UPI QR" 
                    className="w-48 h-48 border-2 border-gray-200 rounded-lg mb-3"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'upi_qr_url')}
                  className="hidden"
                  id="upi-qr-upload"
                />
                <label 
                  htmlFor="upi-qr-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-all"
                >
                  <Upload className="w-4 h-4" />
                  Upload QR Code
                </label>
              </div>
            </div>
          </div>

          {/* Cash Payment Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="text-3xl">💵</div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Cash Payment (Lubumbashi)</h2>
                <p className="text-sm text-gray-600">Payment details for cash payments in Lubumbashi</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Payment Address
                </label>
                <input
                  type="text"
                  value={settings.cash_payment_address || ''}
                  onChange={(e) => setSettings({...settings, cash_payment_address: e.target.value})}
                  placeholder="Enter full address where customers can pay"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Contact Information
                </label>
                <input
                  type="text"
                  value={settings.cash_payment_contact || ''}
                  onChange={(e) => setSettings({...settings, cash_payment_contact: e.target.value})}
                  placeholder="Phone number or contact person name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Additional Instructions
                </label>
                <textarea
                  value={settings.cash_payment_instructions || ''}
                  onChange={(e) => setSettings({...settings, cash_payment_instructions: e.target.value})}
                  placeholder="e.g., Office hours, directions, who to ask for, etc."
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Pricing - DRC */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-8 h-8 text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Pricing - DRC (USD)</h2>
                <p className="text-sm text-gray-600">Set prices for DRC customers in USD</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Teacher Plus</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={settings.drc_plus_monthly}
                      onChange={(e) => setSettings({...settings, drc_plus_monthly: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={settings.drc_plus_annual}
                      onChange={(e) => setSettings({...settings, drc_plus_annual: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Teacher Pro</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={settings.drc_pro_monthly}
                      onChange={(e) => setSettings({...settings, drc_pro_monthly: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={settings.drc_pro_annual}
                      onChange={(e) => setSettings({...settings, drc_pro_annual: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing - India */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <IndianRupee className="w-8 h-8 text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Pricing - India (INR)</h2>
                <p className="text-sm text-gray-600">Set prices for Indian customers in INR</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Teacher Plus</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="1"
                      value={settings.india_plus_monthly}
                      onChange={(e) => setSettings({...settings, india_plus_monthly: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="1"
                      value={settings.india_plus_annual}
                      onChange={(e) => setSettings({...settings, india_plus_annual: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Teacher Pro</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="1"
                      value={settings.india_pro_monthly}
                      onChange={(e) => setSettings({...settings, india_pro_monthly: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="1"
                      value={settings.india_pro_annual}
                      onChange={(e) => setSettings({...settings, india_pro_annual: parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 transition-all inline-flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default AdminPaymentSettings;
